// ═══════════════════════════════════════════════════════════════════════════
// ACDBASSOC2DCONSTRAINTGROUP + ACDBASSOCGEOMDEPENDENCY parser.
// ═══════════════════════════════════════════════════════════════════════════
//
// CONTEXT
// ───────
// AutoCAD's constraint-based parametric blocks expose explicit entity
// back-references for every dimensional constraint parameter via
// `ACDBASSOCGEOMDEPENDENCY` records grouped under an
// `ACDBASSOC2DCONSTRAINTGROUP`. The linkage is indirect:
//
//   BLOCK*CONSTRAINTPARAMETER
//     └─ AcDbBlockConstraintParameter 330 <valueDepHandle>
//                                           │
//                                           ▼
//   ACDBASSOCVALUEDEPENDENCY <valueDepHandle>
//     (sits inside the same constraint group)
//
//   ACDBASSOCGEOMDEPENDENCY
//     │   330 <group handle>            (owner)
//     │   ...
//     │   330 <constrained entity>      (the geometry)
//     │   290 0
//     │   330 <chainHandle1>            (usually the value-dep upstream)
//     │   330 <chainHandle2>            (usually the value-dep downstream)
//     │   360 0
//     │   ...
//     │   100 AcDbAssocGeomDependency
//     │   1   AcDbAssocEdgePersSubentId    (or AcDbAssocSingleEdgePersSubentId)
//     │   90  <subentType>
//     │   90  <subentIdA>
//     │   90  <subentIdB>
//     └   290 0
//
// Matching algorithm:
//   For each constraint parameter, find geom-deps whose chain handles
//   include the parameter's `valueDepHandle`. When the chain-match is
//   ambiguous or missing (e.g. `AcDbAssocSingleEdgePersSubentId` form
//   with no chain handles), fall back to the group-level set — every
//   geom-dep in the same ACDBASSOC2DCONSTRAINTGROUP becomes a candidate.
//
// This module produces TWO flat collections from the OBJECTS section:
//   - `Assoc2dConstraintGroup[]` — one per ACDBASSOC2DCONSTRAINTGROUP,
//     with owner + child dep handles
//   - `AssocGeomDependency[]`    — one per ACDBASSOCGEOMDEPENDENCY,
//     with owner (= group), entity, chain handles, subent IDs
//
// A third collection isn't needed for value-deps — the parameter records
// carry `valueDepHandle` directly, and chain-match is sufficient to link
// them back to the relevant geom-deps.
//
// This module does NOT walk the typed-ordinal graph at the start of the
// constraint group (AcConstrainedBoundedLine / AcConstrainedImplicitPoint
// / AcPointCurveConstraint / …). That graph encodes the solver topology
// AutoCAD uses internally; we don't need it for entity resolution.

import type { DxfGroup } from "@dxf-ai-editor/dxf-io";

import {
  isCurveClass,
  isPointClass,
  isPointCurveConnectorClass,
  parseConstraintGraph,
  type Assoc2dConstraintGroupGraph,
  type ConstraintGraphNode,
} from "./dxfBridge.dynamic.parser.assocdeps.graph";

export type {
  Assoc2dConstraintGroupGraph,
  ConstraintGraphNode,
} from "./dxfBridge.dynamic.parser.assocdeps.graph";

// ═══════════════════════════════════════════════════════════════════════════
// Typed records
// ═══════════════════════════════════════════════════════════════════════════

export interface AssocGeomDependency {
  /** Self handle (group 5). */
  readonly handle: string;
  /**
   * Owner handle (first 330 after the record header) — for geom-deps
   * inside a constraint group this is the group's handle.
   */
  readonly ownerGroupHandle: string;
  /** Handle of the DXF entity (LINE/CIRCLE/LWPOLYLINE/…) being constrained. */
  readonly entityHandle: string;
  /**
   * Upstream / downstream value-dep handles read from the 330 codes
   * inside the AcDbAssocDependency subclass block (excluding the owner
   * 330 and the entity-handle 330). In АБИ1.dxf there are typically two
   * of these; the simpler `AcDbAssocSingleEdgePersSubentId` geom-deps
   * can have zero chain handles.
   */
  readonly chainHandles: readonly string[];
  /**
   * Persistent subent type — first 90 after the `AcDbAssocEdgePersSubentId`
   * or `AcDbAssocSingleEdgePersSubentId` marker. Observed `0` for edge
   * in АБИ1.dxf. `0` is also the default when no PersSubentId block is
   * present (e.g. malformed / stub records).
   */
  readonly subentType: number;
  /** First subent ID (90) after `subentType`. Interpreted as a vertex index on LWPOLYLINE edges. */
  readonly subentIdA: number;
  /** Second subent ID (90) after `subentIdA`. 0 for single-edge form. */
  readonly subentIdB: number;
  /**
   * True when the PersSubentId marker was `AcDbAssocSingleEdgePersSubentId`
   * (no 90 code pairs — the whole entity is the feature). False when the
   * marker was `AcDbAssocEdgePersSubentId` (two 90 codes identify a pair
   * of vertex IDs on an LWPOLYLINE edge). The discriminator is read from
   * the `1` code after the `AcDbAssocGeomDependency` subclass marker,
   * captured during decode.
   */
  readonly isSingleEdge: boolean;
}

export interface Assoc2dConstraintGroup {
  /** Self handle (group 5). */
  readonly handle: string;
  /** Owner BLOCK_RECORD handle (first 330 after the record header). */
  readonly ownerBlockRecord: string;
  /**
   * Every 360 child handle after the typed-ordinal graph — these are
   * the handles of the deps (geom + value) that belong to the group.
   */
  readonly childHandles: readonly string[];
  /**
   * DYN-6 Phase 2 — typed-ordinal graph parsed from the body section
   * AFTER the `360` child list. Populated only when the Section A/B
   * parse succeeds (skip-on-fail contract). When `undefined`, callers
   * fall back to tier-2 chain-match / tier-3 group-fallback. Enables
   * per-parameter entity narrowing (dim-constraint → point → curve).
   */
  readonly graph?: Assoc2dConstraintGroupGraph;
}

// ═══════════════════════════════════════════════════════════════════════════
// Parser entry points
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Pre-scan the OBJECTS section groups and return every ACDBASSOC2DCONSTRAINTGROUP
 * plus every ACDBASSOCGEOMDEPENDENCY found.
 *
 * The input is the raw DxfGroup[] of the OBJECTS section (typically
 * `DxfObjectsSection.rawObjectGroups`). The scanner locates each
 * `code: 0` record header and routes to the appropriate decoder when
 * the class matches.
 */
export function collectAssocConstraintRecords(
  groups: readonly DxfGroup[],
): {
  groups: Map<string, Assoc2dConstraintGroup>;
  geomDeps: Map<string, AssocGeomDependency>;
  /**
   * Generic dependency pivot table built from all ACDBASSOC*DEPENDENCY
   * records (ACDBASSOCDEPENDENCY / ACDBASSOCGEOMDEPENDENCY /
   * ACDBASSOCVALUEDEPENDENCY). Key: dep handle. Value: the full list of
   * 330-code chain handles inside the dep's AcDbAssocDependency subclass
   * block (same definition as `AssocGeomDependency.chainHandles`).
   *
   * Phase 2 Tier 1 uses this to pivot a parameter's `valueDepHandle`
   * (e.g. an ACDBASSOCDEPENDENCY in one constraint group) to the
   * ACDBASSOCDEPENDENCY in the SAME group whose handle appears as a
   * 340 softref in the typed-ordinal graph. Without this pivot the
   * graph's `valueDepToOrdinal` would miss most real AutoCAD files
   * because parameters point to their OWN dependency, not the
   * dependency the graph uses as a softref.
   */
  depChains: Map<string, readonly string[]>;
} {
  const groupMap = new Map<string, Assoc2dConstraintGroup>();
  const geomDepMap = new Map<string, AssocGeomDependency>();
  const depChains = new Map<string, readonly string[]>();

  let current: DxfGroup[] | null = null;
  let currentClass: string | null = null;

  const flush = (): void => {
    if (current === null || currentClass === null) return;
    if (currentClass === "ACDBASSOC2DCONSTRAINTGROUP") {
      const decoded = decodeAssoc2dConstraintGroup(current);
      if (decoded.handle) groupMap.set(decoded.handle, decoded);
    } else if (currentClass === "ACDBASSOCGEOMDEPENDENCY") {
      const decoded = decodeAssocGeomDependency(current);
      if (decoded.handle) {
        geomDepMap.set(decoded.handle, decoded);
        // Include in depChains so a param's valueDepHandle → geom-dep
        // chain resolution works identically to the pivot above.
        depChains.set(decoded.handle, [
          decoded.entityHandle,
          ...decoded.chainHandles,
        ].filter((h) => h.length > 0 && h !== "0"));
      }
    } else if (
      currentClass === "ACDBASSOCDEPENDENCY" ||
      currentClass === "ACDBASSOCVALUEDEPENDENCY"
    ) {
      // Minimal decode — we only need handle + chain handles (330 codes
      // inside AcDbAssocDependency block, excluding the owner 330 and
      // explicit null refs).
      const { handle, chain } = decodeAssocDependencyChain(current);
      if (handle) depChains.set(handle, chain);
    }
  };

  for (const g of groups) {
    if (g.code === 0) {
      flush();
      const type = g.value.trim();
      currentClass =
        type === "ACDBASSOC2DCONSTRAINTGROUP" ||
        type === "ACDBASSOCGEOMDEPENDENCY" ||
        type === "ACDBASSOCDEPENDENCY" ||
        type === "ACDBASSOCVALUEDEPENDENCY"
          ? type
          : null;
      current = currentClass !== null ? [g] : null;
      continue;
    }
    if (current !== null) current.push(g);
  }
  flush();

  return { groups: groupMap, geomDeps: geomDepMap, depChains };
}

/**
 * Minimal decoder for generic ACDBASSOCDEPENDENCY / ACDBASSOCVALUEDEPENDENCY
 * records. Extracts the self-handle (group 5) and the chain handles (all
 * 330 codes AFTER the AcDbAssocDependency subclass marker, excluding the
 * owner 330 and explicit null refs).
 *
 * Used by `collectAssocConstraintRecords` to populate the generic
 * `depChains` pivot table.
 */
function decodeAssocDependencyChain(groups: DxfGroup[]): {
  handle: string;
  chain: string[];
} {
  let handle = "";
  let ownerSeen = false;
  let insideDependencyBlock = false;
  const chain: string[] = [];

  for (const g of groups) {
    switch (g.code) {
      case 5:
        if (handle === "") handle = g.value.trim();
        break;
      case 100:
        if (g.value === "AcDbAssocDependency") {
          insideDependencyBlock = true;
        } else if (insideDependencyBlock) {
          // Subclass marker after the dependency block (e.g.
          // AcDbAssocValueDependency, AcDbAssocGeomDependency) closes
          // the chain window.
          insideDependencyBlock = false;
        }
        break;
      case 330: {
        const h = g.value.trim();
        if (!ownerSeen) {
          ownerSeen = true;
        } else if (insideDependencyBlock && h.length > 0 && h !== "0") {
          chain.push(h);
        }
        break;
      }
      default:
        break;
    }
  }

  return { handle, chain };
}

// ═══════════════════════════════════════════════════════════════════════════
// Per-record decoders (exported for unit tests)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Decode a single `ACDBASSOC2DCONSTRAINTGROUP` body (full groups, including
 * the `0` header line).
 *
 * Structure (observed in АБИ1.dxf handle 4DED):
 *   0   ACDBASSOC2DCONSTRAINTGROUP
 *   5   <self handle>
 *   330 <owner = BLOCK_RECORD handle>
 *   100 AcDbAssocAction                 ← action-subclass block
 *   90 90 330 360 90 90 …                ← assoc action state
 *   100 AcDb2dConstraintGroup            (not emitted as `100` in some
 *                                         AutoCAD builds — the UCS / typed
 *                                         ordinal graph starts right after
 *                                         the action block)
 *   10 20 30 …                           UCS basis
 *   360 0                                first 360 (empty)
 *   90  <child count>
 *   360 <child handle> × N               ← deps owned by this group
 *   90 90 290 90                         post-child metadata
 *   1 AcConstrainedBoundedLine 90 1      typed-ordinal graph (skipped)
 *   …
 *
 * We collect every `360` child handle whose value != "0". The leading
 * `360 0` sentinel is dropped (it's a null-reference slot).
 */
export function decodeAssoc2dConstraintGroup(
  groups: DxfGroup[],
): Assoc2dConstraintGroup {
  let handle = "";
  let ownerBlockRecord = "";
  const childHandles: string[] = [];
  let sawOwner330 = false;
  // DYN-6 Phase 2 — track the index of the LAST `360` child-handle group
  // so the typed-ordinal graph parser knows where Section A begins. We
  // only consider 360 codes AFTER the first non-null one (the initial
  // `360 0` sentinel precedes the real list).
  let lastChild360Idx = -1;
  let sawNonNull360 = false;

  for (let idx = 0; idx < groups.length; idx++) {
    const g = groups[idx];
    if (!g) continue;
    switch (g.code) {
      case 5:
        if (handle === "") handle = g.value.trim();
        break;
      case 330:
        // First 330 = owner (BLOCK_RECORD). Subsequent 330s appear inside
        // the AcDbAssocAction subclass block (e.g. referenced actions) —
        // we don't need them for entity resolution.
        if (!sawOwner330) {
          ownerBlockRecord = g.value.trim();
          sawOwner330 = true;
        }
        break;
      case 360: {
        const h = g.value.trim();
        if (h.length > 0 && h !== "0") {
          childHandles.push(h);
          sawNonNull360 = true;
          lastChild360Idx = idx;
        } else if (!sawNonNull360) {
          // Leading `360 0` sentinel — does NOT advance the child-list
          // end marker (Section A has not started).
        }
        break;
      }
      default:
        break;
    }
  }

  // DYN-6 Phase 2 — attempt typed graph parse after the child list.
  // Skip-on-fail: any parse error yields `undefined` graph and the
  // resolver falls back to tier-2 / tier-3. Requires at least one real
  // 360 child handle (empty groups / malformed records skip graph).
  let graph: Assoc2dConstraintGroupGraph | undefined;
  if (lastChild360Idx >= 0 && lastChild360Idx + 1 < groups.length) {
    const parsed = parseConstraintGraph(groups, lastChild360Idx + 1);
    if (parsed !== null) graph = parsed;
  }

  return {
    handle,
    ownerBlockRecord,
    childHandles,
    graph,
  };
}

/**
 * Decode a single `ACDBASSOCGEOMDEPENDENCY` body (full groups, including
 * the `0` header line).
 *
 * Structure (observed in АБИ1.dxf handle 489F):
 *   0   ACDBASSOCGEOMDEPENDENCY
 *   5   489F
 *   330 4886                 ← owner (constraint group)
 *   100 AcDbAssocDependency
 *   90  2  90 0  290 1 290 1 290 1 290 1  90 -10000
 *   330 48DD                 ← **entity handle** (LWPOLYLINE)
 *   290 0
 *   330 4890                 ← chain handle 1 (upstream value-dep)
 *   330 48B5                 ← chain handle 2 (downstream value-dep)
 *   360 0
 *   90  14
 *   100 AcDbAssocGeomDependency
 *   90  0    290 1
 *   1   AcDbAssocEdgePersSubentId   (or AcDbAssocSingleEdgePersSubentId)
 *   90  0     ← subentType
 *   90  55    ← subentIdA
 *   90  52    ← subentIdB
 *   290 0
 *
 * The simpler `AcDbAssocSingleEdgePersSubentId` form emits just
 *   290 0
 * after the marker — no subent integers. We default subentType / A / B
 * to 0 in that case.
 */
export function decodeAssocGeomDependency(
  groups: DxfGroup[],
): AssocGeomDependency {
  let handle = "";
  let ownerGroupHandle = "";

  // The 330 codes inside the AcDbAssocDependency block (after the owner
  // and subclass marker) appear in a fixed order:
  //   [0] entity handle
  //   [1..] chain handles (upstream / downstream value-deps)
  // We collect them in order and peel off [0] as the entity.
  const depBlock330s: string[] = [];
  let insideDependencyBlock = false;
  let insideGeomDependencyBlock = false;
  let pastPersSubentIdMarker = false;
  let isSingleEdge = false;
  const postSubentIdIntegers: number[] = [];

  for (const g of groups) {
    switch (g.code) {
      case 5:
        if (handle === "") handle = g.value.trim();
        break;
      case 100:
        if (g.value === "AcDbAssocDependency") {
          insideDependencyBlock = true;
          insideGeomDependencyBlock = false;
        } else if (g.value === "AcDbAssocGeomDependency") {
          insideDependencyBlock = false;
          insideGeomDependencyBlock = true;
        }
        break;
      case 330: {
        const h = g.value.trim();
        if (ownerGroupHandle === "") {
          // First 330 = owner (= constraint group handle).
          ownerGroupHandle = h;
        } else if (insideDependencyBlock) {
          // Non-null 330s inside the dep block are the entity + chain
          // handles. Filter out "0" (null references).
          if (h.length > 0 && h !== "0") depBlock330s.push(h);
        }
        break;
      }
      case 1:
        if (
          insideGeomDependencyBlock &&
          (g.value === "AcDbAssocEdgePersSubentId" ||
            g.value === "AcDbAssocSingleEdgePersSubentId" ||
            g.value === "AcDbAssocFacePersSubentId" ||
            g.value === "AcDbAssocPersSubentId")
        ) {
          pastPersSubentIdMarker = true;
          // Discriminate between the two PersSubentId forms. Only the
          // `AcDbAssocSingleEdgePersSubentId` form carries no 90 code
          // pairs and refers to the entity as a whole. The plain
          // `AcDbAssocEdgePersSubentId` form (observed in АБИ1.dxf
          // LWPOLYLINE edges) carries two 90 codes naming the vertex
          // pair. `AcDbAssocFace*` and the abstract base class are
          // treated as non-single for now (Phase-1 scope).
          if (g.value === "AcDbAssocSingleEdgePersSubentId") {
            isSingleEdge = true;
          }
        }
        break;
      case 90:
        if (insideGeomDependencyBlock && pastPersSubentIdMarker) {
          const n = Number.parseInt(g.value.trim(), 10);
          if (Number.isFinite(n)) postSubentIdIntegers.push(n);
        }
        break;
      default:
        break;
    }
  }

  const entityHandle = depBlock330s[0] ?? "";
  const chainHandles = depBlock330s.slice(1);

  const subentType = postSubentIdIntegers[0] ?? 0;
  const subentIdA = postSubentIdIntegers[1] ?? 0;
  const subentIdB = postSubentIdIntegers[2] ?? 0;

  return {
    handle,
    ownerGroupHandle,
    entityHandle,
    chainHandles,
    subentType,
    subentIdA,
    subentIdB,
    isSingleEdge,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Matching algorithm
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Find the geom-deps owned by the given constraint group whose chain
 * handles include `valueDepHandle`. When the chain-match yields no
 * results, return every geom-dep in the group as a fallback (group-level
 * over-approximation — documented in module header).
 *
 * The caller supplies a `geomDepsByGroup` map (group handle → list of
 * geom-deps) for O(1) lookup. `ownerGroupHandle` is the handle of the
 * ACDBASSOC2DCONSTRAINTGROUP that contains both the parameter's
 * value-dep and the geom-deps we're searching.
 */
export function findAssociatedGeomDeps(
  valueDepHandle: string,
  ownerGroupHandle: string,
  geomDepsByGroup: ReadonlyMap<string, readonly AssocGeomDependency[]>,
): {
  matched: readonly AssocGeomDependency[];
  source: "chain" | "group-fallback" | "none";
} {
  const groupDeps = geomDepsByGroup.get(ownerGroupHandle) ?? [];
  if (groupDeps.length === 0) {
    return { matched: [], source: "none" };
  }

  if (valueDepHandle.length > 0) {
    const chainMatched = groupDeps.filter((dep) =>
      dep.chainHandles.includes(valueDepHandle),
    );
    if (chainMatched.length > 0) {
      return { matched: chainMatched, source: "chain" };
    }
  }

  // Fallback: over-approximate with every geom-dep in the group. This
  // keeps anchor inference functional even when the chain linkage
  // didn't resolve (e.g. parameter records without a value-dep handle
  // or `AcDbAssocSingleEdgePersSubentId`-form geom-deps).
  return { matched: groupDeps, source: "group-fallback" };
}

/**
 * Build a `groupHandle → geomDep[]` index from the flat geom-dep map.
 * Used by the bridge layer to avoid a per-parameter scan over every
 * geom-dep in the file.
 */
export function indexGeomDepsByGroup(
  geomDeps: ReadonlyMap<string, AssocGeomDependency>,
): Map<string, AssocGeomDependency[]> {
  const out = new Map<string, AssocGeomDependency[]>();
  for (const dep of geomDeps.values()) {
    if (dep.ownerGroupHandle.length === 0) continue;
    const list = out.get(dep.ownerGroupHandle);
    if (list) {
      list.push(dep);
    } else {
      out.set(dep.ownerGroupHandle, [dep]);
    }
  }
  return out;
}

/**
 * Build a `childHandle → groupHandle` reverse index from the flat
 * constraint-group map. Lets the bridge resolve "which group owns this
 * value-dep?" in O(1) when matching parameters to their geom-deps.
 *
 * Works for both geom-dep handles and value-dep handles since
 * Assoc2dConstraintGroup.childHandles lists every dep (regardless of
 * dep kind) under its 360 codes.
 */
export function indexChildToGroup(
  groups: ReadonlyMap<string, Assoc2dConstraintGroup>,
): Map<string, string> {
  const out = new Map<string, string>();
  for (const group of groups.values()) {
    for (const child of group.childHandles) {
      if (!out.has(child)) out.set(child, group.handle);
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Associated-entity ref resolution
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Associated-entity ref shape used by the bridge layer to populate
 * constraint-parameter `associatedEntities[]`. Structurally identical
 * to `AssociatedEntityRef` from `dynamicBlockParameters.ts`; duplicated
 * here to avoid a circular import (drawing-model schema cannot reach
 * into the parser tree).
 */
export interface ResolvedAssocEntityRef {
  readonly entityId: string;
  readonly subentType: number;
  readonly subentIdA: number;
  readonly subentIdB: number;
  /**
   * True when the geom-dep's PersSubentId marker was the Single-edge
   * form (whole LINE / CIRCLE / ARC etc.). False for the plain Edge
   * form (LWPOLYLINE edge between two vertex IDs). Used by the block-
   * evaluator `resolveSubselector` helper to pick the canonical
   * subselector (`""` vs `"edge<i>"` vs `"center"`).
   */
  readonly isSingleEdge: boolean;
}

/**
 * DYN-6 Phase 2 — per-parameter narrowing via the typed-ordinal graph.
 *
 * When the constraint group carries a parsed typed graph (Section A/B),
 * walk dim-constraint → point → curve to produce the PRECISE set of
 * entity handles bound to a given value-dep handle (typically 1-3
 * entities per parameter). This replaces the group-level over-
 * approximation (39 entities for the ibeam's "b" parameter) with the
 * actual subset of geom-deps that wire that parameter to its curves.
 *
 * Returns `null` when:
 *   - the group has no parsed graph (skip-on-fail path)
 *   - the `valueDepHandle` does not appear as a 340 softref of any
 *     dim-constraint node in the graph
 *   - the walk yields no curve entity handles (orphan dim-constraint)
 *   - no geom-deps in `geomDeps` match the narrowed entity set
 *
 * In all "null" cases the caller falls back to tier-2 (chain-match) and
 * tier-3 (group-fallback). Returning `null` is the skip-on-fail signal;
 * returning `[]` would incorrectly claim "zero entities" and break
 * downstream anchor inference.
 */
export function resolveAssociatedEntitiesViaGraph(
  valueDepHandle: string,
  group: Assoc2dConstraintGroup,
  geomDeps: readonly AssocGeomDependency[],
  depChains?: ReadonlyMap<string, readonly string[]>,
): readonly ResolvedAssocEntityRef[] | null {
  const graph = group.graph;
  if (!graph) return null;

  // Direct match: valueDepHandle is itself the 340 softref used by the
  // graph. Simple fixture / test case path.
  let dimOrd = graph.valueDepToOrdinal.get(valueDepHandle);

  // Pivot match: real AutoCAD parameters reference an
  // ACDBASSOCDEPENDENCY in one group, but the typed-graph's 340 softref
  // points at a DIFFERENT dep (usually in the same group) which chains
  // back to the parameter's dep. We walk the chain once in each
  // direction: check every handle in depChains[valueDepHandle], then
  // the inverse — every dep whose chain contains valueDepHandle.
  if (dimOrd === undefined && depChains) {
    const ownChain = depChains.get(valueDepHandle);
    if (ownChain) {
      for (const h of ownChain) {
        const ord = graph.valueDepToOrdinal.get(h);
        if (ord !== undefined) {
          dimOrd = ord;
          break;
        }
      }
    }
    if (dimOrd === undefined) {
      for (const [otherHandle, chain] of depChains) {
        if (chain.includes(valueDepHandle)) {
          const ord = graph.valueDepToOrdinal.get(otherHandle);
          if (ord !== undefined) {
            dimOrd = ord;
            break;
          }
        }
      }
    }
  }

  if (dimOrd === undefined) return null;

  const dimNode = graph.nodesByOrdinal.get(dimOrd);
  if (!dimNode) return null;

  // Walk: dim-constraint → point → PointCurveConstraint → curve entity.
  //
  // AutoCAD's typical constraint graph wires each point-on-curve through
  // an explicit `AcPointCurveConstraint` connector node rather than
  // linking the point's refs directly to a curve. We accept any of these
  // edge shapes:
  //   - point → PointCurveConstraint → curve   (common case, 95% of АБИ1)
  //   - point → curve                          (fallback for horizontal /
  //                                             vertical constraints that
  //                                             bind directly)
  //   - dim → curve                            (when the dim-constraint
  //                                             itself refs a curve, rare)
  const entityHandles = new Set<string>();
  const collectEntityFromNode = (node: ConstraintGraphNode): void => {
    const h = node.entityHandle;
    if (h.length > 0 && h !== "0") entityHandles.add(h);
  };

  for (const ptOrd of dimNode.refs) {
    const pt = graph.nodesByOrdinal.get(ptOrd);
    if (!pt) continue;
    if (isPointClass(pt.className)) {
      for (const midOrd of pt.refs) {
        const mid = graph.nodesByOrdinal.get(midOrd);
        if (!mid) continue;
        if (isPointCurveConnectorClass(mid.className)) {
          // Hop through PointCurveConstraint to its curve refs.
          for (const cOrd of mid.refs) {
            const c = graph.nodesByOrdinal.get(cOrd);
            if (c && isCurveClass(c.className)) collectEntityFromNode(c);
          }
        } else if (isCurveClass(mid.className)) {
          collectEntityFromNode(mid);
        }
        // Other mid-classes (PointCoincidenceConstraint, MidPointConstraint,
        // FixedConstraint, other dims) don't contribute entities directly.
      }
    } else if (isCurveClass(pt.className)) {
      collectEntityFromNode(pt);
    }
  }

  if (entityHandles.size === 0) return null;

  // Narrow the geom-deps to only those that match the walked set. The
  // graph's `330 <handle>` for curve bodies is NOT the drawing-entity
  // handle — AutoCAD stores the ACDBASSOCGEOMDEPENDENCY handle there
  // (that dep then carries the real LWPOLYLINE / LINE / ARC handle in
  // its own body). So the narrowed set is matched by geom-dep HANDLE
  // first; the deprecated `entityHandle` fallback is kept for
  // fixture-shape tests where the graph already carries the entity
  // handle directly.
  const narrowed = geomDeps.filter(
    (dep) =>
      dep.ownerGroupHandle === group.handle &&
      (entityHandles.has(dep.handle) || entityHandles.has(dep.entityHandle)),
  );
  if (narrowed.length === 0) return null;

  return narrowed.map((dep) => ({
    entityId: dep.entityHandle,
    subentType: dep.subentType,
    subentIdA: dep.subentIdA,
    subentIdB: dep.subentIdB,
    isSingleEdge: dep.isSingleEdge,
  }));
}

/**
 * Given a parameter's `valueDepHandle` and the pre-built indices,
 * produce the `associatedEntities[]` list to attach to the parameter.
 *
 * Three-tier algorithm (DYN-6 Phase 2 adds Tier 1):
 *   1. Tier 1 — `resolveAssociatedEntitiesViaGraph` — precise per-param
 *      narrowing via the typed-ordinal graph (dim → point → curve walk).
 *      Typically yields 1-3 entity handles per parameter.
 *   2. Tier 2 — chain-match: geom-deps whose `chainHandles` include
 *      `valueDepHandle`. Usually narrows well when the solver emitted
 *      explicit upstream/downstream chain refs.
 *   3. Tier 3 — group-level fallback: every geom-dep in the owning
 *      constraint group. Over-approximation kept as a safety net.
 *
 * Emits a `[DYN-6]` console debug line indicating the tier used (kept
 * minimal — no per-param spam unless telemetry env var is set).
 */
export function resolveAssociatedEntities(
  valueDepHandle: string,
  childToGroup: ReadonlyMap<string, string>,
  geomDepsByGroup: ReadonlyMap<string, readonly AssocGeomDependency[]>,
  groupsByHandle?: ReadonlyMap<string, Assoc2dConstraintGroup>,
  depChains?: ReadonlyMap<string, readonly string[]>,
): readonly ResolvedAssocEntityRef[] {
  if (valueDepHandle.length === 0) return [];

  // Build the list of CANDIDATE group handles to search. The direct
  // childToGroup lookup returns the group that lists `valueDepHandle` as
  // a 360 child (usually where the parameter's own ACDBASSOCDEPENDENCY
  // lives). But the graph with the dim-constraint 340 softref may live
  // in a DIFFERENT constraint group reachable via chain pivot — e.g.
  // АБИ1.dxf ibeam parameter `b` has valueDep=48BE in group 4886 which
  // chains to 4F00 in group 4ED7 where the graph has `4F00` as 340
  // softref. We try the direct group first, then pivot via depChains.
  const candidateGroups: string[] = [];
  const seenGroups = new Set<string>();
  const addCandidate = (h: string | undefined): void => {
    if (h !== undefined && !seenGroups.has(h)) {
      seenGroups.add(h);
      candidateGroups.push(h);
    }
  };
  addCandidate(childToGroup.get(valueDepHandle));
  if (depChains) {
    const ownChain = depChains.get(valueDepHandle);
    if (ownChain) {
      for (const h of ownChain) addCandidate(childToGroup.get(h));
    }
    // Also add groups owning deps that chain BACK to valueDepHandle
    // (inverse direction). Bounded to keep the scan cheap.
    for (const [otherHandle, chain] of depChains) {
      if (chain.includes(valueDepHandle)) {
        addCandidate(childToGroup.get(otherHandle));
      }
    }
  }
  if (candidateGroups.length === 0) return [];

  // ── Tier 1: typed-ordinal graph walk across all candidate groups ──
  if (groupsByHandle) {
    for (const gh of candidateGroups) {
      const group = groupsByHandle.get(gh);
      if (!group || !group.graph) continue;
      const groupDeps = geomDepsByGroup.get(gh) ?? [];
      const tier1 = resolveAssociatedEntitiesViaGraph(
        valueDepHandle,
        group,
        groupDeps,
        depChains,
      );
      if (tier1 !== null && tier1.length > 0) {
        logTier("graph", valueDepHandle, tier1.length);
        return tier1;
      }
    }
  }

  // ── Tier 2: chain-match (any candidate group) ──
  const primaryGroup = candidateGroups[0]!;
  const { matched, source } = findAssociatedGeomDeps(
    valueDepHandle,
    primaryGroup,
    geomDepsByGroup,
  );
  if (matched.length > 0) {
    logTier(source, valueDepHandle, matched.length);
  }
  return matched.map((dep) => ({
    entityId: dep.entityHandle,
    subentType: dep.subentType,
    subentIdA: dep.subentIdA,
    subentIdB: dep.subentIdB,
    isSingleEdge: dep.isSingleEdge,
  }));
}

/**
 * Telemetry helper — emits a `[DYN-6]` debug line naming the tier that
 * produced refs for a parameter. Silent in production unless
 * `process.env.DYN_6_TRACE === "1"` (node-only; browser ignores it).
 */
function logTier(
  source: "graph" | "chain" | "group-fallback" | "none",
  valueDepHandle: string,
  refCount: number,
): void {
  try {
    if (
      typeof process !== "undefined" &&
      process.env !== undefined &&
      process.env.DYN_6_TRACE === "1"
    ) {
      console.debug(
        `[DYN-6] tier=${source} valueDep=${valueDepHandle} refs=${refCount}`,
      );
    }
  } catch {
    // process may be undefined in some browser sandboxes — silently skip.
  }
}
